import React, {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useRouter } from 'next/router';

import CenteredCircularProgress from '@watershed/ui-core/components/CenteredCircularProgress';

// CoPilot UI configuration interfaces
interface CoPilotUIConfig {
  featureId: string;
  shouldActivate: (pathname: string) => boolean;
  component: React.ComponentType<{ onClose: () => void }>;
}

// We do this because we don't know the type of the component props and we want to be flexible (for now). See also below.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ComponentModule = { default: React.ComponentType<any> };

interface CoPilotRegistration {
  featureId: string;
  shouldActivate: (pathname: string) => boolean;
  componentLoader: () => Promise<ComponentModule>;
  componentProps?: Record<string, unknown>;
}

// Event-driven CoPilot registry with subscription pattern
class CoPilotRegistry {
  private registrations = new Map<string, CoPilotRegistration>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private loadedComponents = new Map<string, React.ComponentType<any>>();
  private listeners = new Set<() => void>();

  register(registration: CoPilotRegistration) {
    this.registrations.set(registration.featureId, registration);

    // Notify all listeners of registration change
    this.notifyListeners();
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => listener());
  }

  unregister(featureId: string) {
    this.registrations.delete(featureId);
    this.loadedComponents.delete(featureId);
  }

  async getActiveConfig(
    pathname: string,
    asPath: string
  ): Promise<CoPilotUIConfig | null> {
    for (const [featureId, registration] of this.registrations) {
      if (
        registration.shouldActivate(pathname) ||
        registration.shouldActivate(asPath)
      ) {
        // Load component if not already loaded
        if (!this.loadedComponents.has(featureId)) {
          try {
            const loadedModule = await registration.componentLoader();
            this.loadedComponents.set(featureId, loadedModule.default);
          } catch (error) {
            console.error(
              `Failed to load CoPilot component for ${featureId}:`,
              error
            );
            continue;
          }
        }

        const LoadedComponent = this.loadedComponents.get(featureId);
        if (!LoadedComponent) continue;

        return {
          featureId,
          shouldActivate: registration.shouldActivate,
          component: ({ onClose }) => {
            // Map onClose to component-specific prop names
            const componentProps = { ...(registration.componentProps || {}) };
            if ('onCollapsedChange' in componentProps) {
              componentProps.onCollapsedChange = onClose;
            }

            return (
              <Suspense fallback={<CenteredCircularProgress />}>
                <LoadedComponent onClose={onClose} {...componentProps} />
              </Suspense>
            );
          },
        };
      }
    }
    return null;
  }

  getAllRegistrations(): Array<CoPilotRegistration> {
    return Array.from(this.registrations.values());
  }
}

// Global registry instance
const coPilotRegistry = new CoPilotRegistry();

/**
 * Example of how teams can register their own CoPilots:
 *
 * import { registerCoPilot } from '@/components/ai-service/CoPilotContext';
 *
 * registerCoPilot({
 *   featureId: 'finance-assistant',
 *   shouldActivate: (pathname) => pathname.startsWith('/finance/'),
 *   componentLoader: () => import('./FinanceAssistantCoPilot'),
 *   componentProps: {
 *     theme: 'finance',
 *     showAnalytics: true,
 *   },
 * });
 */

// Utility function for teams to easily register CoPilots
export const registerCoPilot = (registration: CoPilotRegistration) => {
  coPilotRegistry.register(registration);
};

// Utility function to unregister CoPilots
export const unregisterCoPilot = (featureId: string) => {
  coPilotRegistry.unregister(featureId);
};

// Export registry for advanced use cases
export { coPilotRegistry };

/**
 * CoPilot Context for managing route-based CoPilot rendering
 */
interface CoPilotContextType {
  activeCoPilot: CoPilotUIConfig | null;
  isCoPilotOpen: boolean;
  toggleCoPilot: () => void;
  openCoPilot: () => void;
  closeCoPilot: () => void;
}

// Create the context with default values
const CoPilotContext = createContext<CoPilotContextType>({
  activeCoPilot: null,
  isCoPilotOpen: false,
  toggleCoPilot: () => {},
  openCoPilot: () => {},
  closeCoPilot: () => {},
});

export function CoPilotProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [isCoPilotOpen, setIsCoPilotOpen] = useState(false);
  const [activeCoPilot, setActiveCoPilot] = useState<CoPilotUIConfig | null>(
    null
  );

  // Load active CoPilot based on route changes
  const loadActiveCoPilot = useCallback(async () => {
    try {
      const config = await coPilotRegistry.getActiveConfig(
        router.pathname,
        router.asPath
      );
      // Only update if featureId changed - this keeps component identity stable
      // and prevents remounting when only query params change (e.g., tab switching)
      setActiveCoPilot((prev) => {
        if (prev?.featureId === config?.featureId) {
          return prev;
        }
        return config;
      });
    } catch {
      // For now we can silently fail - if CoPilot fails to load, we just don't show it.
      setActiveCoPilot(null);
    }
  }, [router.pathname, router.asPath]);

  // Subscribe to registry changes and route changes
  useEffect(() => {
    // Load on mount and route changes
    void loadActiveCoPilot();

    // Subscribe to registry changes (when new CoPilots register)
    const unsubscribe = coPilotRegistry.subscribe(() => {
      void loadActiveCoPilot();
    });

    return unsubscribe;
  }, [loadActiveCoPilot]);

  // CoPilot control functions
  const toggleCoPilot = useCallback(() => {
    setIsCoPilotOpen((prev) => !prev);
  }, []);

  const openCoPilot = useCallback(() => {
    setIsCoPilotOpen(true);
  }, []);

  const closeCoPilot = useCallback(() => {
    setIsCoPilotOpen(false);
  }, []);

  // Auto-close when no active copilot
  useEffect(() => {
    if (!activeCoPilot && isCoPilotOpen) {
      setIsCoPilotOpen(false);
    }
  }, [activeCoPilot, isCoPilotOpen]);

  // Create the context value
  const contextValue = useMemo(
    () => ({
      activeCoPilot,
      isCoPilotOpen,
      toggleCoPilot,
      openCoPilot,
      closeCoPilot,
    }),
    [activeCoPilot, isCoPilotOpen, toggleCoPilot, openCoPilot, closeCoPilot]
  );

  return (
    <CoPilotContext.Provider value={contextValue}>
      {children}
    </CoPilotContext.Provider>
  );
}

/**
 * Custom hook to access CoPilot UI state and controls
 */
export function useCoPilot() {
  const context = useContext(CoPilotContext);

  if (context === undefined) {
    throw new Error('useCoPilot must be used within a CoPilotProvider');
  }

  return {
    activeCoPilot: context.activeCoPilot,
    isCoPilotOpen: context.isCoPilotOpen,
    toggleCoPilot: context.toggleCoPilot,
    openCoPilot: context.openCoPilot,
    closeCoPilot: context.closeCoPilot,
  };
}

/**
 * Export types for teams to use
 */
export type { CoPilotUIConfig, CoPilotRegistration };
